import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarMonth,
  InputGroup,
  InputGroupItem,
  TextInput,
  TimePicker,
  Button,
  Popover,
} from '@patternfly/react-core';
import OutlinedCalendarAltIcon from '@patternfly/react-icons/dist/esm/icons/outlined-calendar-alt-icon';
import { DateTime } from 'luxon';

interface DateTimeWidgetProps {
  value?: string;
  onChange?: (value: string) => void;
  id?: string;
  disabled?: boolean;
  readonly?: boolean;
}

const DATE_DISPLAY_FORMAT = 'MM-dd-yyyy';

function parseISOtoLocal(iso: string | undefined) {
  if (!iso) return { date: '', time: '' };
  const dt = DateTime.fromISO(iso, { zone: 'local' });
  if (!dt.isValid) return { date: '', time: '' };
  return {
    date: dt.toFormat(DATE_DISPLAY_FORMAT),
    time: dt.toFormat('HH:mm'),
  };
}

function combineDateTimeToISO(
  datePart: string,
  timePart: string,
): string | null {
  if (!datePart) return null;
  const dt = DateTime.fromFormat(
    `${datePart} ${timePart || '00:00'}`,
    `${DATE_DISPLAY_FORMAT} HH:mm`,
    { zone: 'local' },
  );
  return dt.isValid ? dt.toISO() : null;
}

const formatDateForDisplay = (date: Date): string =>
  date
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replaceAll('/', '-');

const DateTimePicker: React.FunctionComponent<DateTimeWidgetProps> = ({
  value,
  onChange,
  id,
  disabled,
  readonly,
}) => {
  const initialParsed = parseISOtoLocal(value);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [valueDate, setValueDate] = useState(initialParsed.date);
  const [valueTime, setValueTime] = useState(initialParsed.time);

  useEffect(() => {
    const parsed = parseISOtoLocal(value);
    setValueDate(parsed.date);
    setValueTime(parsed.time);
  }, [value]);

  const emitChange = useCallback(
    (datePart: string, timePart: string) => {
      if (!onChange) return;
      const iso = combineDateTimeToISO(datePart, timePart);
      if (iso) onChange(iso);
    },
    [onChange],
  );

  const onToggleCalendar = () => {
    setIsCalendarOpen((prev) => !prev);
  };

  const onSelectCalendar = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    newValueDate: Date,
  ) => {
    const formatted = formatDateForDisplay(newValueDate);
    setValueDate(formatted);
    setIsCalendarOpen(false);
    emitChange(formatted, valueTime);
  };

  const onTimeChange = (
    _event: React.FormEvent<HTMLInputElement>,
    time: string,
  ) => {
    setValueTime(time);
    emitChange(valueDate, time);
  };

  const calendarDate = valueDate
    ? DateTime.fromFormat(valueDate, DATE_DISPLAY_FORMAT)
    : undefined;

  const calendar = (
    <CalendarMonth
      date={calendarDate?.isValid ? calendarDate.toJSDate() : new Date()}
      onChange={onSelectCalendar}
    />
  );

  const calendarButton = (
    <Button
      variant="control"
      aria-label="Toggle the calendar"
      onClick={onToggleCalendar}
      isDisabled={disabled || readonly}
      icon={<OutlinedCalendarAltIcon />}
    />
  );

  return (
    <div style={{ width: '350px' }}>
      <Popover
        position="bottom"
        bodyContent={calendar}
        showClose={false}
        isVisible={isCalendarOpen}
        hasNoPadding
        hasAutoWidth
      >
        <InputGroup>
          <InputGroupItem>
            <TextInput
              type="text"
              id={id || 'date-time'}
              aria-label="date picker"
              value={valueDate}
              readOnlyVariant="default"
            />
          </InputGroupItem>
          <InputGroupItem>{calendarButton}</InputGroupItem>
          <InputGroupItem>
            <TimePicker
              time={valueTime}
              onChange={onTimeChange}
              is24Hour
              stepMinutes={15}
              placeholder="HH:MM"
              isDisabled={disabled || readonly}
              style={{ width: '150px' }}
            />
          </InputGroupItem>
        </InputGroup>
      </Popover>
    </div>
  );
};

export default DateTimePicker;
